import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Conges, ControleUL, Evenement, RFIntervalle } from '@estel-numerique/galaclick-api';
import {
    DateHelper, EvenementHelper, ProtocoleEnum,
    ServiceHelper, TypeService
} from '@estel-numerique/galaclick-core';
import { PlanJourEveType } from './plan-jour-eve-type.model';

export class PlanJourEve {
    public evenement: Evenement;
    public eveVideRef: PlanJourEve;
    public dateDebut: Date;
    public dateDebutAff: Date;
    public dateFin: Date;
    public dateFinAff: Date;
    public nbJours: number;
    public nbDemiesJourneesEntamees: number;
    public isDebutMatin: boolean;
    public isFinMatin: boolean;
    public hasExtraInfos: boolean;
    public ecartMax: number;
    public ecartMoy: number;
    public protocole: string;
    public frequence: string;
    public pctOccupation: number;
    public dateEvtRealise: string;
    public isRobot: boolean;

    public cssClasse: string;
    public cssStyle: string;
    public type: PlanJourEveType;
    public hasInfobulle: boolean;

    private constructor(type: PlanJourEveType) {
        this.type = type;
    }

    static genereEveVide(eveVideRef: PlanJourEve): PlanJourEve {
        const eveVide: PlanJourEve = new PlanJourEve(PlanJourEveType.VIDE);
        eveVide.cssClasse = 'eve-vide';
        eveVide.hasInfobulle = false;
        eveVide.eveVideRef = eveVideRef;
        return eveVide;
    }

    /**
     *
     * @param evenement
     * @param dernierControleUL
     * @param listeIntervalles
     * @param anneeMoisCible
     * @param debutPremierEvenement facultatif, n'est utilisé que si l'evenement actuel n'a pas la même date de début
     * @returns
     */
    static genereEveAgent(
        evenement: Evenement,
        dernierControleUL: ControleUL,
        listeIntervalles: RFIntervalle[],
        anneeMoisCible: string,
        dernierEvenement?: Evenement,
        debutFirstEvenement?: string): PlanJourEve {

        // Calcule les dates
        const DDEBUT: Date = DateHelper.fConvtoDate(evenement.DDEBUT);
        const DFIN: Date = DateHelper.fConvtoDate(evenement.DFIN);
        // utilisé seulement pour le calcul de l'écart
        const debutFirstEve = debutFirstEvenement ? DateHelper.fConvtoDate(debutFirstEvenement) : null;

        // Détermine la couleur à appliquer selon le service
        let cssClasse: string;
        switch (evenement.SERVICE.TYPESERVICE.CODESERV) {
            case TypeService.Analyse:
                cssClasse = ' eve-plan-general eve-ANA';
                break;
            case TypeService.DataHub:
                cssClasse = 'eve-HUB';
                break;
            case TypeService.Mastimir:
                cssClasse = 'eve-MAS';
                break;
            case TypeService.OriAutomate:
                cssClasse = 'eve-ORI';
                break;
            case TypeService.Pesee:
                cssClasse = ' eve-plan-general eve-PES';
                break;
            case TypeService.Profilage:
                cssClasse = 'eve-PRO';
                break;
            case TypeService.Activite:
                cssClasse = 'eve-SIG';
                break;
            default:
                cssClasse = 'eve-XXX';
        }

        // Récupère les informations complémentaires si besoin
        const hasExtraInfos = (ServiceHelper.isServicePesee(evenement.SERVICE) === true);
        let ecartMax: number;
        let ecartMoy: number;
        let protocole: string;
        let frequence: string;
        let isRobot: boolean;
        if (hasExtraInfos === true) {
            if (dernierControleUL != null) {
                ecartMax = EvenementHelper.getEcartMax(listeIntervalles, dernierControleUL, debutFirstEve ? debutFirstEve : DDEBUT, dernierEvenement);
                ecartMoy = EvenementHelper.getEcartMoy(listeIntervalles, dernierControleUL, debutFirstEve ? debutFirstEve : DDEBUT, dernierEvenement);

                const REMESU = (dernierControleUL.REMESU || '');
                const TROBCL = (dernierControleUL.TROBCL || '');
                if (REMESU !== '' || TROBCL !== '') {
                    protocole = REMESU + TROBCL;
                }
                isRobot = TROBCL?.includes(ProtocoleEnum.R__PRT_ROBOT_TRAITE);
                frequence = dernierControleUL.FRPACL;
            }
        }

        const eveAgent: PlanJourEve = new PlanJourEve(PlanJourEveType.EVENEMENT);
        eveAgent.isRobot = isRobot;
        eveAgent.evenement = evenement;
        eveAgent.dateDebut = DDEBUT;
        eveAgent.dateFin = DFIN;
        eveAgent.hasExtraInfos = hasExtraInfos;
        eveAgent.ecartMax = ecartMax;
        eveAgent.ecartMoy = ecartMoy;
        eveAgent.protocole = protocole;
        eveAgent.frequence = frequence;
        if (evenement.dateRealise == null) {
            cssClasse += ' eveRealise';
        }
        if (eveAgent.isRobot) {
            cssClasse += ' eve-ROBOT';
        }
        eveAgent.cssClasse = cssClasse;
        eveAgent.hasInfobulle = true;
        eveAgent.dateEvtRealise = DateHelper.fConvDateFR(evenement.dateRealise);



        return this._calculeDonnees(eveAgent, anneeMoisCible);
    }

    static genereEveConge(
        conges: Conges,
        anneeMoisCible: string): PlanJourEve {

        // Calcule les dates
        const DDEBUT: Date = DateHelper.fConvtoDate(conges.DDEBUT);
        const DFIN: Date = DateHelper.fConvtoDate(conges.DFIN);

        const eveConges: PlanJourEve = new PlanJourEve(PlanJourEveType.CONGES);
        eveConges.dateDebut = DDEBUT;
        eveConges.dateFin = DFIN;
        eveConges.cssClasse = 'eve-conges';
        eveConges.hasInfobulle = true;

        return this._calculeDonnees(eveConges, anneeMoisCible);
    }

    private static _calculeDonnees(planJourEve: PlanJourEve, anneeMoisCible: string) {
        // Calcule la date de début et de fin d'affichage de l'évènement
        // Cas particulier : si l'évènement a commencé ou se termine un autre mois, on tronque
        let isEveMoisPrec = false;
        let isEveMoisSuiv = false;
        let dateDebutAff = new Date(planJourEve.dateDebut);
        let dateFinAff = new Date(planJourEve.dateFin);
        if (DateHelper.dateToAnneeMois(planJourEve.dateDebut) < anneeMoisCible) {
            dateDebutAff = DateHelper.premierJourAnneeMois(anneeMoisCible);
            isEveMoisPrec = true;
        }
        if (DateHelper.dateToAnneeMois(planJourEve.dateFin) > anneeMoisCible) {
            dateFinAff = DateHelper.changeTimeSlotFromComponents(DateHelper.dernierJourAnneeMois(anneeMoisCible), 23, 59, 59, 0);
            isEveMoisSuiv = true;
        }
        const isDebutMatin = dateDebutAff.getHours() <= 12;
        const isFinMatin = dateFinAff.getHours() <= 12;
        dateDebutAff = DateHelper.changeTimeSlotFromComponents(dateDebutAff, (isDebutMatin ? 0 : 13), 0, 0, 0);
        dateFinAff = DateHelper.changeTimeSlotFromComponents(dateFinAff, (isFinMatin ? 12 : 23), 59, 59, 0);

        // En déduit les données d'affichage
        const nbJours = DateHelper.dayDiff(dateFinAff, dateDebutAff);
        const nbDemiesJourneesEntamees = Math.round((dateFinAff.getTime() - dateDebutAff.getTime()) / 43200000.0);
        const cssClasse =
            (planJourEve.cssClasse || '') + ' eve-base' +
            (isEveMoisPrec === true ? ' eve-mois-prec' : '') +
            (isEveMoisSuiv === true ? ' eve-mois-suiv' : '');
        const cssStyle =
            "";

        planJourEve.dateDebutAff = dateDebutAff;
        planJourEve.dateFinAff = dateFinAff;
        planJourEve.nbJours = nbJours;
        planJourEve.nbDemiesJourneesEntamees = nbDemiesJourneesEntamees;
        planJourEve.isDebutMatin = isDebutMatin;
        planJourEve.isFinMatin = isFinMatin;
        planJourEve.cssClasse = cssClasse;
        planJourEve.cssStyle = cssStyle;
        return planJourEve;
    }

    public getCssStyleSanitized(sanitizer: DomSanitizer): SafeHtml {
        const cssStyle = (this.cssStyle || '') + '--eveHeight: ' + String(this.pctOccupation) + '%;';
        return sanitizer.bypassSecurityTrustStyle(cssStyle);
    }
}
